import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { commonKey, DateSelectedValue } from '@/utils/constant';
import { cn } from '@/utils/utils';
import { format } from 'date-fns';
import { ArrowRight, CalendarIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { TZDate } from 'react-day-picker';
import ButtonSelect from './button-select';

const { enDateTimeFormat } = commonKey;

export type TZDateRange = {
  from: TZDate | undefined;
  to?: TZDate | undefined;
};

export function DatePickerWithRange(props: {
  className?: string;
  startDate: TZDate;
  endDate: TZDate;
  timeZone: string;
  onDateChange: (startDate: TZDate, endDate: TZDate) => void;
}) {
  const [date, setDate] = React.useState<TZDateRange | undefined>({
    from: new TZDate(new Date(), props.timeZone),
    to: new TZDate(new Date(), props.timeZone),
  });
  const [isOpen, setIsOpen] = useState(false);

  const handleDateSelect = (range: TZDateRange | undefined) => {
    setDate(range);
  };
  const handleOkClick = () => {
    if (date?.from && date?.to) {
      props.onDateChange(date.from, date.to);
    }
    setIsOpen(false);
  };
  const handleCancelClick = () => {
    setDate(date);
    setIsOpen(false);
  };
  useEffect(() => {
    setDate({ from: props.startDate, to: props.endDate });
  }, [props.startDate, props.endDate]);

  return (
    <div className={cn('grid h-full gap-2', props.className)}>
      <Popover open={isOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={'outline'}
            className={cn(
              'h-full w-[300px] flex-none rounded-[1.875rem] border border-gray-lighter bg-gray-lightest px-6 py-3 text-base font-normal',
              !date && 'text-muted-foreground',
            )}
            onClick={() => setIsOpen(true)}
          >
            <CalendarIcon />

            {date?.from && date?.to && (
              <>
                {format(date.from, enDateTimeFormat)} <ArrowRight />
                {format(date.to, enDateTimeFormat)}
              </>
            )}

            {date?.from && !date?.to && format(date.from, enDateTimeFormat)}

            {!date?.from && !date?.to && <span>Pick a date</span>}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            timeZone={'America/Menominee'}
            defaultMonth={date?.from}
            selected={date}
            onSelect={(range) => {
              handleDateSelect({
                from: range?.from
                  ? new TZDate(range.from, props.timeZone)
                  : undefined,
                to: range?.to
                  ? new TZDate(range.to, props.timeZone)
                  : undefined,
              });
            }}
            numberOfMonths={2}
          />
          <div className="flex justify-between gap-2 p-4">
            <Button variant="outline" onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button
              onClick={handleOkClick}
              disabled={!(date?.from && date?.to)}
            >
              OK
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

const SelectedDate = (props: {
  startDate: TZDate;
  endDate: TZDate;
  timeZone: string;
  selectedValue: DateSelectedValue;
  onChange: (startDate: TZDate, endDate: TZDate) => void;
}) => {
  const handleDateChange = (startDate: TZDate, endDate: TZDate) => {
    props.onChange(startDate, endDate);
  };
  return (
    <>
      <DatePickerWithRange
        startDate={props.startDate}
        endDate={props.endDate}
        timeZone={props.timeZone}
        onDateChange={handleDateChange}
      />

      <ButtonSelect
        value={props.selectedValue}
        timezone={props.timeZone}
        onChange={(start, end) => {
          props.onChange(start, end);
        }}
      />
    </>
  );
};

export default SelectedDate;
